import * as React from 'react';

import useUrlState from '@ahooksjs/use-url-state';
import { useGetState, useMemoizedFn, useRequest } from 'ahooks';
import { Helmet } from 'react-helmet-async';

import { searchPurchases } from 'services';
import type { PaymentGatewayType, PurchaseStatus } from 'types';
import { PATHNAME } from 'utils';

import { PurchasesHeader, PurchasesTable } from './components';

const {
  QUERY_PARAM: {
    DEFAULT: {
      SORT_TYPE: DEFAULT_SORT_TYPE,
      PAGE_NUMBER: DEFAULT_PAGE_NUMBER,
      PAGE_SIZE: DEFAULT_PAGE_SIZE,
    },
    SORT_TYPE: { BASE_URL: SORT_TYPE_BASE_URL },
    SEARCH_KEY: { BASE_URL: SEARCH_KEY_BASE_URL },
    PAGE_NUMBER: { BASE_URL: PAGE_NUMBER_BASE_URL },
  },
} = PATHNAME;

export default function Purchases() {
  const [
    {
      [SORT_TYPE_BASE_URL]: sortType,
      [SEARCH_KEY_BASE_URL]: searchKey,
      [PAGE_NUMBER_BASE_URL]: pageNumber,
    },
    setUrlState,
  ] = useUrlState(
    {
      [SORT_TYPE_BASE_URL]: DEFAULT_SORT_TYPE,
      [SEARCH_KEY_BASE_URL]: '',
      [PAGE_NUMBER_BASE_URL]: DEFAULT_PAGE_NUMBER,
    },
    { navigateMode: 'replace' },
  );
  const [pageSize, setPageSize] = useGetState<number>(DEFAULT_PAGE_SIZE);
  const [statuses, setStatuses] = useGetState<PurchaseStatus[]>();
  const [paymentGatewayType, setPaymentGatewayType] =
    useGetState<PaymentGatewayType>();
  const [lowerBoundCreateDate, setLowerBoundCreateDate] = useGetState<string>();
  const [upperBoundCreateDate, setUpperBoundCreateDate] = useGetState<string>();
  const { data, loading } = useRequest(
    async () =>
      await searchPurchases({
        sortType,
        searchKey,
        pageNumber,
        pageSize,
        statuses,
        paymentGatewayType,
        lowerBoundCreateDate,
        upperBoundCreateDate,
      }),
    {
      refreshDeps: [
        sortType,
        searchKey,
        pageNumber,
        pageSize,
        statuses,
        paymentGatewayType,
        lowerBoundCreateDate,
        upperBoundCreateDate,
      ],
    },
  );

  const { total = 0, data: purchases = [] } = data ?? {};

  const handleSearchKey = useMemoizedFn((updatedSearchKey: string) => {
    setUrlState({ [SEARCH_KEY_BASE_URL]: updatedSearchKey });
  });
  const handlePageNumber = useMemoizedFn((updatedPageNumber: number) => {
    setUrlState({ [PAGE_NUMBER_BASE_URL]: updatedPageNumber });
  });
  const handlePageSize = useMemoizedFn((updatedPageSize: number) => {
    setPageSize(updatedPageSize);
  });
  const handleStatuses = useMemoizedFn((updatedStatuses?: PurchaseStatus[]) => {
    setStatuses(updatedStatuses);
  });
  const handlePaymentGatewayType = useMemoizedFn(
    (updatedPaymentGatewayType?: PaymentGatewayType) => {
      setPaymentGatewayType(updatedPaymentGatewayType);
    },
  );
  const handleLowerBoundCreateDate = useMemoizedFn(
    (updatedLowerBoundCreateDate?: string) => {
      setLowerBoundCreateDate(updatedLowerBoundCreateDate);
    },
  );
  const handleUpperBoundCreateDate = useMemoizedFn(
    (updatedUpperBoundCreateDate?: string) => {
      setUpperBoundCreateDate(updatedUpperBoundCreateDate);
    },
  );

  return (
    <React.Fragment>
      <Helmet>
        <title>سفارش‌ها | کاشی لند</title>
      </Helmet>
      <div className='rounded bg-white p-6'>
        <PurchasesHeader
          searchKey={searchKey}
          handleSearchKey={handleSearchKey}
          handleLowerBoundCreateDate={handleLowerBoundCreateDate}
          handleUpperBoundCreateDate={handleUpperBoundCreateDate}
        />
        <PurchasesTable
          loading={loading}
          total={total}
          purchases={purchases}
          pageSize={pageSize}
          pageNumber={pageNumber}
          handleStatuses={handleStatuses}
          handlePageSize={handlePageSize}
          handlePageNumber={handlePageNumber}
          handlePaymentGatewayType={handlePaymentGatewayType}
        />
      </div>
    </React.Fragment>
  );
}
